/**
 * /account/billing — Native in-app billing portal.
 *
 * Three tabs:
 *  • Overview   — subscription status + payment method summary
 *  • Invoices   — paginated list of past invoices with PDF download
 *  • Payment    — embedded Stripe CardElement to update payment method
 *
 * Stripe API calls go through Supabase Edge Functions — no client-side
 * secret key is ever needed. Stripe.js is loaded only for the Payment tab.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  CreditCard, CheckCircle2, AlertTriangle, XCircle, Loader2,
  RefreshCw, ArrowRight, Clock, Download, Receipt, Lock,
  ChevronRight,
} from "lucide-react";
import { format, fromUnixTime, isPast } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import {
  useSubscription,
  useCancelSubscriptionImmediate,
  useGetBillingDetails,
  useUpdatePaymentMethod,
  type StripeInvoice,
  type StripePaymentMethod,
} from "@/hooks/useSubscription";
import { useToast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/analytics";

// ─── Stripe.js (loaded once lazily) ──────────────────────────────────────────

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? "");

// ─── Status helpers ───────────────────────────────────────────────────────────

function statusBadge(status: string, cancelAtPeriodEnd: boolean) {
  if (cancelAtPeriodEnd && status === "active") {
    return <Badge variant="outline" className="border-amber-500 text-amber-600">Cancels at period end</Badge>;
  }
  switch (status) {
    case "active":    return <Badge className="bg-green-600 hover:bg-green-600">Active</Badge>;
    case "trialing":  return <Badge className="bg-blue-600 hover:bg-blue-600">Trial</Badge>;
    case "past_due":  return <Badge variant="destructive">Past due</Badge>;
    case "unpaid":    return <Badge variant="destructive">Unpaid</Badge>;
    case "canceled":  return <Badge variant="outline" className="text-muted-foreground">Canceled</Badge>;
    case "incomplete": return <Badge variant="outline" className="border-amber-500 text-amber-600">Incomplete</Badge>;
    default:          return <Badge variant="outline">{status}</Badge>;
  }
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case "active":
    case "trialing":  return <CheckCircle2 className="h-5 w-5 text-green-600" />;
    case "past_due":
    case "unpaid":    return <AlertTriangle className="h-5 w-5 text-amber-500" />;
    case "canceled":  return <XCircle className="h-5 w-5 text-muted-foreground" />;
    default:          return <Clock className="h-5 w-5 text-muted-foreground" />;
  }
}

// Card brand → display label
function brandLabel(brand: string) {
  const map: Record<string, string> = {
    visa: "Visa", mastercard: "Mastercard", amex: "American Express",
    discover: "Discover", jcb: "JCB", unionpay: "UnionPay",
    diners: "Diners Club",
  };
  return map[brand.toLowerCase()] ?? brand;
}

// Stripe amounts are in cents
function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

// ─── Invoice status badge ─────────────────────────────────────────────────────

function invoiceStatusBadge(status: string | null) {
  switch (status) {
    case "paid":   return <Badge className="bg-green-600 hover:bg-green-600 text-xs">Paid</Badge>;
    case "open":   return <Badge variant="outline" className="border-amber-500 text-amber-600 text-xs">Open</Badge>;
    case "void":   return <Badge variant="outline" className="text-muted-foreground text-xs">Void</Badge>;
    case "draft":  return <Badge variant="outline" className="text-xs">Draft</Badge>;
    default:       return <Badge variant="outline" className="text-xs">{status ?? "—"}</Badge>;
  }
}

// ─── Payment method card icon ─────────────────────────────────────────────────

function CardBrandIcon({ brand }: { brand: string }) {
  const b = brand.toLowerCase();
  // Simple coloured pill — replace with real SVG logos if desired
  const colours: Record<string, string> = {
    visa: "bg-blue-600",
    mastercard: "bg-orange-500",
    amex: "bg-sky-500",
    discover: "bg-orange-400",
  };
  return (
    <span className={`inline-flex items-center justify-center rounded px-1.5 py-0.5 text-[10px] font-bold text-white ${colours[b] ?? "bg-gray-500"}`}>
      {brandLabel(brand).slice(0, 4).toUpperCase()}
    </span>
  );
}

// ─── Update Payment Method form (uses Stripe Elements) ───────────────────────

interface UpdateCardFormProps {
  userId: string;
  onSuccess: (pm: StripePaymentMethod) => void;
}

function UpdateCardForm({ userId, onSuccess }: UpdateCardFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const updatePaymentMethod = useUpdatePaymentMethod();
  const { toast } = useToast();
  const [isConfirming, setIsConfirming] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsConfirming(true);
    setCardError(null);

    try {
      // 1. Get a SetupIntent client_secret from our Edge Function
      const res = await supabase.functions.invoke("create-setup-intent", {
        body: { userId },
      });
      if (res.error) {
        const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
        throw new Error(detail);
      }
      const { clientSecret, error: fnError } = res.data as {
        clientSecret?: string; error?: string;
      };
      if (fnError) throw new Error(fnError);
      if (!clientSecret) throw new Error("No client secret returned");

      // 2. Confirm the card setup client-side with Stripe.js
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error("Card element not mounted");

      const { setupIntent, error: stripeError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardElement },
      });
      if (stripeError) throw new Error(stripeError.message ?? "Card confirmation failed");
      if (!setupIntent?.payment_method) throw new Error("No payment method returned from Stripe");

      // 3. Tell our backend to set this as the default payment method
      const pm = await updatePaymentMethod.mutateAsync({
        userId,
        paymentMethodId: setupIntent.payment_method as string,
      });

      toast({
        title: "✅ Payment method updated",
        description: `${brandLabel(pm.brand)} ···· ${pm.last4} is now your default card.`,
      });
      onSuccess(pm);
    } catch (err) {
      const msg = (err as Error).message ?? "Unknown error";
      setCardError(msg);
      toast({ title: "Card update failed", description: msg, variant: "destructive" });
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-lg border bg-card p-4 shadow-sm">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: "15px",
                color: "#0f172a",
                fontFamily: "Inter, system-ui, sans-serif",
                "::placeholder": { color: "#94a3b8" },
              },
              invalid: { color: "#dc2626" },
            },
          }}
          onChange={(e) => setCardError(e.error?.message ?? null)}
        />
      </div>

      {cardError && (
        <p className="text-sm text-destructive flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          {cardError}
        </p>
      )}

      <Button type="submit" disabled={!stripe || isConfirming} className="w-full sm:w-auto">
        {isConfirming ? (
          <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving card…</>
        ) : (
          <><Lock className="mr-2 h-4 w-4" />Save Payment Method</>
        )}
      </Button>

      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <Lock className="h-3 w-3" />
        Secured by Stripe — your card details never touch our servers.
      </p>
    </form>
  );
}

// ─── Invoices tab ─────────────────────────────────────────────────────────────

function InvoicesTab({ invoices, isLoading }: {
  invoices: StripeInvoice[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (invoices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
        <Receipt className="h-10 w-10 text-muted-foreground/40" />
        <p className="text-muted-foreground text-sm">No invoices yet</p>
        <p className="text-xs text-muted-foreground/60">
          Your invoices will appear here after your first billing cycle.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/50">
          <tr>
            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Date</th>
            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground hidden sm:table-cell">Invoice #</th>
            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground">Amount</th>
            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground hidden md:table-cell">Status</th>
            <th className="px-4 py-2.5" />
          </tr>
        </thead>
        <tbody className="divide-y">
          {invoices.map((inv) => (
            <tr key={inv.id} className="hover:bg-muted/30 transition-colors">
              <td className="px-4 py-3 text-muted-foreground">
                {format(fromUnixTime(inv.created), "MMM d, yyyy")}
              </td>
              <td className="px-4 py-3 font-mono text-xs text-muted-foreground hidden sm:table-cell">
                {inv.number ?? inv.id.slice(0, 12)}
              </td>
              <td className="px-4 py-3 font-medium">
                {formatCurrency(inv.status === "paid" ? inv.amountPaid : inv.amountDue, inv.currency)}
              </td>
              <td className="px-4 py-3 hidden md:table-cell">
                {invoiceStatusBadge(inv.status)}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-2">
                  {inv.invoicePdf && (
                    <a
                      href={inv.invoicePdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Download PDF"
                    >
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                  )}
                  {inv.hostedUrl && (
                    <a
                      href={inv.hostedUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="View invoice"
                    >
                      <Button variant="ghost" size="icon" className="h-7 w-7">
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Billing() {
  const { user } = useAuth();
  const { subscription, isActive, requiresSubscription, isLoading, refetch } = useSubscription();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isSyncing, setIsSyncing] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [activationError, setActivationError] = useState<string | null>(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const cancelImmediate = useCancelSubscriptionImmediate();
  const hasActivatedRef = useRef(false);

  // Billing details (invoices + payment method)
  const hasSubscription = !!subscription;
  const billingDetails = useGetBillingDetails(user?.id, hasSubscription);

  const paymentMethod = billingDetails.data?.paymentMethod ?? null;
  const invoices = billingDetails.data?.invoices ?? [];
  const upcoming = billingDetails.data?.upcoming ?? null;

  /** Sync subscription status from Stripe into Supabase, then refetch. */
  const syncSubscription = async () => {
    if (!user?.id) return;
    setIsSyncing(true);
    try {
      const res = await supabase.functions.invoke("sync-subscription", {
        body: { userId: user.id },
      });
      if (res.error) {
        const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
        throw new Error(detail);
      }
    } catch (err) {
      console.warn("[Billing] sync-subscription error:", err);
    } finally {
      await refetch();
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    const sessionId = searchParams.get("session_id");

    if (sessionId) {
      if (hasActivatedRef.current) return;
      hasActivatedRef.current = true;

      setIsActivating(true);
      (async () => {
        try {
          const res = await supabase.functions.invoke("activate-subscription", {
            body: { sessionId, userId: user.id },
          });
          if (res.error) {
            const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
            throw new Error(detail);
          }
          const { error } = res.data as { error?: string };
          if (error) throw new Error(error);

          // Fire analytics events for successful payment/subscription start
          trackEvent("subscription_started", { user_id: user.id });
          trackEvent("payment_succeeded", { user_id: user.id, source: "checkout" });

          toast({
            title: "🎉 Subscription activated!",
            description: "Your subscription is now active. Welcome to Stratakey STR Compliance.",
          });
          navigate("/account/billing", { replace: true });
          await refetch();
        } catch (err) {
          const msg = (err as Error).message ?? "Unknown error";
          console.error("[Billing] activate-subscription error:", msg);
          setActivationError(msg);
          await syncSubscription();
        } finally {
          setIsActivating(false);
        }
      })();
    } else {
      syncSubscription();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleCancelImmediately = () => {
    if (!user?.id || !subscription?.stripe_subscription_id) return;
    cancelImmediate.mutate(
      { subscriptionId: subscription.stripe_subscription_id, userId: user.id },
      {
        onSuccess: (result) => {
          setShowCancelDialog(false);
          // Track cancellation in PostHog
          trackEvent("subscription_cancelled", {
            user_id: user?.id ?? undefined,
            refund_type: result.refundType ?? "none",
          });
          let title: string;
          let description: string;
          switch (result.refundType) {
            case "full":
              title = "✅ Subscription cancelled";
              description = `Full refund of ${result.refundAmountDisplay} issued.`;
              break;
            case "prorated":
              title = "✅ Subscription cancelled";
              description = `Prorated refund of ${result.refundAmountDisplay} issued.`;
              break;
            case "trial":
              title = "ℹ️ Trial cancelled";
              description = "Your free trial has been cancelled. No charge was made.";
              break;
            default:
              title = "ℹ️ Subscription cancelled";
              description = "Your subscription has been cancelled.";
              break;
          }
          toast({ title, description });
          syncSubscription();
        },
        onError: (err) => {
          setShowCancelDialog(false);
          toast({
            title: "❌ Cancellation failed",
            description: err.message,
            variant: "destructive",
          });
        },
      }
    );
  };

  const handleSubscribe = () => navigate("/pricing");

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ── Host / system_admin ───────────────────────────────────────────────────
  if (!requiresSubscription) {
    return (
      <div className="max-w-2xl mx-auto py-10 px-4 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Billing</h1>
          <p className="text-muted-foreground text-sm mt-1">Subscription management</p>
        </div>
        <Separator />
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>No subscription required</AlertTitle>
          <AlertDescription>
            Your role (<strong className="capitalize">{user?.role?.replace("_", " ")}</strong>)
            does not require a paid subscription.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // ── No subscription ───────────────────────────────────────────────────────
  if (!subscription) {
    if (isActivating || isSyncing) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            {isActivating ? "Activating your subscription…" : "Syncing subscription…"}
          </p>
        </div>
      );
    }

    const sessionId = searchParams.get("session_id");

    return (
      <div className="max-w-2xl mx-auto py-10 px-4 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Billing</h1>
          <p className="text-muted-foreground text-sm mt-1">Subscription management</p>
        </div>
        <Separator />

        {sessionId && activationError ? (
          <>
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Subscription activation failed</AlertTitle>
              <AlertDescription className="space-y-1">
                <span className="block">Your payment was received but we couldn't activate your subscription automatically.</span>
                <span className="block text-xs font-mono opacity-80">{activationError}</span>
              </AlertDescription>
            </Alert>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => {
                  setActivationError(null);
                  hasActivatedRef.current = false;
                  if (!user?.id) return;
                  setIsActivating(true);
                  (async () => {
                    try {
                      const res = await supabase.functions.invoke("activate-subscription", {
                        body: { sessionId, userId: user.id },
                      });
                      if (res.error) {
                        const detail = (res.data as { error?: string } | null)?.error ?? res.error.message;
                        throw new Error(detail);
                      }
                      toast({ title: "🎉 Subscription activated!" });
                      await refetch();
                    } catch (err) {
                      setActivationError((err as Error).message);
                      await syncSubscription();
                    } finally {
                      setIsActivating(false);
                    }
                  })();
                }}
                disabled={isActivating}
              >
                {isActivating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Activating…</> : <><RefreshCw className="mr-2 h-4 w-4" />Retry Activation</>}
              </Button>
              <Button variant="outline" onClick={() => { setActivationError(null); syncSubscription(); }} disabled={isSyncing}>
                {isSyncing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Syncing…</> : <><RefreshCw className="mr-2 h-4 w-4" />Refresh Status</>}
              </Button>
            </div>
          </>
        ) : (
          <>
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>No active subscription</AlertTitle>
              <AlertDescription>You need a subscription to access Stratakey STR Compliance features.</AlertDescription>
            </Alert>
            <Button onClick={handleSubscribe}>
              <CreditCard className="mr-2 h-4 w-4" />Choose a Plan<ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </>
        )}
      </div>
    );
  }

  // ── Active subscription — full tabbed portal ──────────────────────────────
  const { current_period_end, trial_end, status, cancel_at_period_end } = subscription;
  const periodEnd = current_period_end ? new Date(current_period_end) : null;
  const trialEnd = trial_end ? new Date(trial_end) : null;
  const isTrialing = status === "trialing";
  const isPastDue = status === "past_due" || status === "unpaid";

  const MANAGER_PRICE_IDS = new Set(
    [
      import.meta.env.VITE_STRIPE_PRICE_MANAGER_MONTHLY,
      import.meta.env.VITE_STRIPE_PRICE_MANAGER_YEARLY,
    ].filter(Boolean)
  );
  const roleName =
    subscription?.price_id && MANAGER_PRICE_IDS.has(subscription.price_id)
      ? "Property Manager"
      : user?.role === "property_manager"
      ? "Property Manager"
      : "Strata Council";

  return (
    <div className="max-w-3xl mx-auto py-10 px-4 space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Billing</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage your Stratakey subscription
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={syncSubscription} disabled={isSyncing} className="gap-1.5 shrink-0">
          {isSyncing ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Syncing…</> : <><RefreshCw className="h-3.5 w-3.5" />Refresh</>}
        </Button>
      </div>

      <Separator />

      {/* Alerts */}
      {isPastDue && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Payment failed</AlertTitle>
          <AlertDescription>
            Your last payment didn't go through. Please update your payment method in the{" "}
            <button onClick={() => setActiveTab("payment")} className="underline font-medium">Payment tab</button>.
          </AlertDescription>
        </Alert>
      )}

      {cancel_at_period_end && periodEnd && (
        <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-amber-800 dark:text-amber-300">Subscription ending soon</AlertTitle>
          <AlertDescription className="text-amber-700 dark:text-amber-400">
            Access until <strong>{format(periodEnd, "MMMM d, yyyy")}</strong>. After that date you will lose access.
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="overview" className="flex-1 sm:flex-none">Overview</TabsTrigger>
          <TabsTrigger value="invoices" className="flex-1 sm:flex-none">Invoices</TabsTrigger>
          <TabsTrigger value="payment" className="flex-1 sm:flex-none">Payment Method</TabsTrigger>
        </TabsList>

        {/* ── Overview tab ─────────────────────────────────────────────────── */}
        <TabsContent value="overview" className="mt-6 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                    <CreditCard className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-base">{roleName} Plan</CardTitle>
                    <CardDescription className="text-xs">Stratakey STR Compliance Platform</CardDescription>
                  </div>
                </div>
                {statusBadge(status, cancel_at_period_end)}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Separator />

              <div className="grid grid-cols-2 gap-y-3 text-sm">
                <span className="text-muted-foreground">Status</span>
                <span className="flex items-center gap-1.5 font-medium capitalize">
                  <StatusIcon status={status} />
                  {status.replace(/_/g, " ")}
                </span>

                <span className="text-muted-foreground">Plan</span>
                <span className="font-medium">{roleName}</span>

                {isTrialing && trialEnd && (
                  <>
                    <span className="text-muted-foreground">Trial ends</span>
                    <span className="font-medium">
                      {format(trialEnd, "MMMM d, yyyy")}
                      {isPast(trialEnd) ? " (ended)" : ""}
                    </span>
                  </>
                )}

                {periodEnd && (
                  <>
                    <span className="text-muted-foreground">
                      {cancel_at_period_end ? "Access until" : "Renews on"}
                    </span>
                    <span className="font-medium">{format(periodEnd, "MMMM d, yyyy")}</span>
                  </>
                )}

                {/* Payment method summary */}
                <span className="text-muted-foreground">Payment</span>
                <span className="flex items-center gap-2">
                  {billingDetails.isLoading ? (
                    <Skeleton className="h-4 w-32" />
                  ) : paymentMethod ? (
                    <>
                      <CardBrandIcon brand={paymentMethod.brand} />
                      <span className="font-medium">···· {paymentMethod.last4}</span>
                      <span className="text-muted-foreground text-xs">
                        {paymentMethod.expMonth.toString().padStart(2, "0")}/{String(paymentMethod.expYear).slice(-2)}
                      </span>
                    </>
                  ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                  <button
                    onClick={() => setActiveTab("payment")}
                    className="text-xs text-primary underline-offset-2 hover:underline ml-1"
                  >
                    Update
                  </button>
                </span>

                {/* Upcoming invoice */}
                {upcoming && (
                  <>
                    <span className="text-muted-foreground">Next charge</span>
                    <span className="font-medium">
                      {formatCurrency(upcoming.amountDue, upcoming.currency)}
                      {upcoming.nextPaymentAttempt && (
                        <span className="text-muted-foreground text-xs ml-1.5">
                          on {format(fromUnixTime(upcoming.nextPaymentAttempt), "MMM d")}
                        </span>
                      )}
                    </span>
                  </>
                )}

                {subscription.stripe_customer_id && (
                  <>
                    <span className="text-muted-foreground">Customer ID</span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {subscription.stripe_customer_id}
                    </span>
                  </>
                )}
              </div>

              <Separator />

              <div className="flex flex-wrap gap-3 pt-1">
                {(status === "active" || status === "trialing") && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowCancelDialog(true)}
                    disabled={cancelImmediate.isPending}
                    className="gap-1.5"
                  >
                    {cancelImmediate.isPending ? (
                      <><Loader2 className="h-3.5 w-3.5 animate-spin" />Canceling…</>
                    ) : isTrialing ? (
                      "Cancel Free Trial"
                    ) : (
                      "Cancel & Get Refund"
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <p className="text-xs text-muted-foreground text-center">
            Want to compare plans?{" "}
            <Link to="/pricing" className="underline hover:text-foreground">View pricing →</Link>
          </p>
        </TabsContent>

        {/* ── Invoices tab ─────────────────────────────────────────────────── */}
        <TabsContent value="invoices" className="mt-6">
          <InvoicesTab
            invoices={invoices}
            isLoading={billingDetails.isLoading}
          />
          {billingDetails.isError && (
            <Alert variant="destructive" className="mt-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Could not load invoices</AlertTitle>
              <AlertDescription>{billingDetails.error?.message}</AlertDescription>
            </Alert>
          )}
        </TabsContent>

        {/* ── Payment method tab ────────────────────────────────────────────── */}
        <TabsContent value="payment" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Update Payment Method</CardTitle>
              <CardDescription>
                Your new card will be used for all future subscription renewals.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Current card summary */}
              {paymentMethod && (
                <div className="flex items-center gap-3 rounded-lg border bg-muted/30 px-4 py-3 text-sm">
                  <CardBrandIcon brand={paymentMethod.brand} />
                  <span className="font-medium">Current: ···· {paymentMethod.last4}</span>
                  <span className="text-muted-foreground text-xs ml-auto">
                    Expires {paymentMethod.expMonth.toString().padStart(2, "0")}/{String(paymentMethod.expYear).slice(-2)}
                  </span>
                </div>
              )}

              {/* Stripe Elements form */}
              <Elements stripe={stripePromise}>
                <UpdateCardForm
                  userId={user!.id}
                  onSuccess={(pm) => {
                    // Optimistically update the UI before cache invalidation resolves
                    setActiveTab("overview");
                  }}
                />
              </Elements>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Cancel dialog */}
      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isTrialing ? "Cancel free trial?" : "Cancel subscription immediately?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                Your subscription will be canceled <strong>right now</strong> and you will
                lose access immediately.
              </span>
              {isTrialing ? (
                <span className="block">
                  No payment has been collected, so no refund is needed.
                </span>
              ) : (
                <span className="block">
                  You'll receive a <strong>prorated refund</strong> for unused days (typically 5–10 business days).
                </span>
              )}
              <span className="block text-destructive font-medium">This action cannot be undone.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelImmediate.isPending}>Keep subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleCancelImmediately(); }}
              disabled={cancelImmediate.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {cancelImmediate.isPending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Canceling…</>
              ) : "Yes, cancel & refund"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
